import { useCallback, useEffect, useState } from "react";

/** Docked drawer on wide screens: full (icons and labels), icons only, or not shown. */
export type NavDrawerMode = "expanded" | "mini" | "hidden";

export const NAV_DRAWER_MODE_KEY = "studio.navDrawer.mode";

/** Widths in px the page content shifts by for each docked mode. */
export const NAV_DRAWER_WIDTHS: Record<NavDrawerMode, number> = { expanded: 280, mini: 68, hidden: 0 };

const MODES: NavDrawerMode[] = ["expanded", "mini", "hidden"];

function storageKey(userId: string | undefined): string {
    return `${NAV_DRAWER_MODE_KEY}:${userId || "anonymous"}`;
}

export function readNavDrawerMode(userId: string | undefined): NavDrawerMode {
    try {
        const stored = localStorage.getItem(storageKey(userId)) as NavDrawerMode | null;
        return stored && MODES.includes(stored) ? stored : "expanded";
    } catch {
        return "expanded";
    }
}

/** The docked drawer's mode, remembered per user so it stays how they left it. */
export function useNavDrawerMode(userId: string | undefined): [NavDrawerMode, (mode: NavDrawerMode) => void] {
    const [mode, setModeState] = useState<NavDrawerMode>(() => readNavDrawerMode(userId));

    // The profile often loads after the first render; pick up that user's choice when it does.
    useEffect(() => {
        setModeState(readNavDrawerMode(userId));
    }, [userId]);

    const setMode = useCallback(
        (next: NavDrawerMode) => {
            setModeState(next);
            try {
                localStorage.setItem(storageKey(userId), next);
            } catch {
                // Storage can be unavailable; the choice just won't stick.
            }
        },
        [userId]
    );

    return [mode, setMode];
}
